import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { reportAPI } from '../services/api';

const RANGES = [
  { value: 'last_7_days', label: '7D' },
  { value: 'last_30_days', label: '30D' },
  { value: 'last_90_days', label: '90D' },
  { value: 'current_month', label: 'This Month' },
  { value: 'last_month', label: 'Last Month' },
  { value: 'current_year', label: 'This Year' },
  { value: 'all', label: 'All Time' },
  { value: 'custom', label: 'Custom' }
];

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'performance', label: 'Recruiter Performance' },
  { id: 'regional', label: 'Regional' },
  { id: 'growth', label: 'Growth Trends' }
];

function downloadCSV(rows, headers, filename) {
  if (!rows.length) {
    toast.info('No data to export');
    return;
  }
  const content = [headers, ...rows]
    .map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(','))
    .join('\n');
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
  toast.success(`${filename} downloaded`);
}

function KpiTile({ label, value, delta, accent }) {
  const hasDelta = typeof delta === 'number' && !Number.isNaN(delta);
  const positive = delta >= 0;
  return (
    <div className={`stat-card${accent ? ' accent' : ''}`}>
      <div className="stat-card-title">{label}</div>
      <div className="stat-card-value">{value}</div>
      {hasDelta && (
        <div style={{ marginTop: 6, fontSize: 12, fontWeight: 600, color: positive ? 'var(--accent-green)' : '#ef4444' }}>
          {positive ? '+' : ''}{delta}% <span style={{ color: 'var(--text-muted)', fontWeight: 500 }}>vs prev period</span>
        </div>
      )}
    </div>
  );
}

export default function ReportsSection({ title = 'Reports', subtitle = 'Generate and export detailed reports with date-range filters' }) {
  const [range, setRange] = useState('last_30_days');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    if (range === 'custom' && (!startDate || !endDate)) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const params = { range };
    if (range === 'custom') {
      params.startDate = startDate;
      params.endDate = endDate;
    }
    reportAPI.getDashboard(params)
      .then(res => { if (!cancelled) setData(res.data || res); })
      .catch(() => toast.error('Failed to load report data'))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [range, startDate, endDate]);

  const overview = data?.overview || {};
  const deltas = data?.deltas || {};
  const topRecruiters = data?.topRecruiters || [];
  const regionalData = data?.regionalData || [];
  const monthly = data?.monthlyPerformance || [];

  const periodLabel = data?.period ? `${data.period.start} → ${data.period.end}` : '';

  const renderTabContent = () => {
    if (loading) {
      return <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--text-secondary)' }}>Loading report data…</div>;
    }
    if (!data) {
      return <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-secondary)' }}>No data available for the selected period.</div>;
    }

    if (activeTab === 'overview') {
      const kpis = [
        { label: 'Active Recruiters', value: overview.activeRecruiters || 0 },
        { label: 'Admissions', value: overview.totalAdmissions || 0, delta: deltas.admissions },
        { label: 'Enrollments', value: overview.totalEnrollments || 0, delta: deltas.enrollments },
        { label: 'Total Revenue', value: `KES ${Number(overview.totalCommissions || 0).toFixed(2)}`, delta: deltas.revenue },
        { label: 'Pending Payments', value: overview.pendingPayments || 0 }
      ];
      const summaryRows = [
        ['Active Recruiters', overview.activeRecruiters || 0],
        ['Total Admissions', overview.totalAdmissions || 0],
        ['Total Enrollments', overview.totalEnrollments || 0],
        ['Total Revenue', `KES ${Number(overview.totalCommissions || 0).toFixed(2)}`],
        ['Pending Payments', overview.pendingPayments || 0],
        ['Pending Amount', `KES ${Number(overview.pendingAmount || 0).toFixed(2)}`]
      ];
      return (
        <div>
          <div className="kpi-grid">
            {kpis.map(k => <KpiTile key={k.label} {...k} />)}
          </div>
          <div className="card" style={{ marginTop: '24px' }}>
            <div className="card-header">
              <div><h2 className="card-title">Summary</h2><p className="card-subtitle">Key metrics for the selected period</p></div>
              <button className="btn btn-outline btn-sm" onClick={() => downloadCSV(summaryRows, ['Metric', 'Value'], `report_summary_${new Date().toISOString().split('T')[0]}.csv`)}>Export CSV</button>
            </div>
            <div className="table-container">
              <table className="table">
                <thead><tr><th>Metric</th><th>Value</th></tr></thead>
                <tbody>
                  {summaryRows.map((r, i) => (
                    <tr key={i}><td style={{ fontWeight: 600 }}>{r[0]}</td><td>{r[1]}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      );
    }

    if (activeTab === 'performance') {
      const rows = topRecruiters.map(r => [r.full_name, r.referral_code, r.admission_count, r.enrollment_count, `KES ${Number(r.total_commission || 0).toFixed(2)}`]);
      return (
        <div className="card">
          <div className="card-header">
            <div><h2 className="card-title">Recruiter Performance</h2><p className="card-subtitle">Top recruiters ranked by commission (max 10)</p></div>
            <button className="btn btn-outline btn-sm" onClick={() => downloadCSV(rows, ['Recruiter', 'Referral Code', 'Admissions', 'Enrollments', 'Commission'], `recruiter_performance_${new Date().toISOString().split('T')[0]}.csv`)}>Export CSV</button>
          </div>
          <div className="table-container">
            <table className="table">
              <thead><tr><th>#</th><th>Recruiter</th><th>Referral Code</th><th>Admissions</th><th>Enrollments</th><th>Commission</th></tr></thead>
              <tbody>
                {topRecruiters.length ? topRecruiters.map((r, i) => (
                  <tr key={r.id}>
                    <td>{i + 1}</td>
                    <td style={{ fontWeight: 600 }}>{r.full_name}</td>
                    <td><span className="badge badge-info">{r.referral_code}</span></td>
                    <td>{r.admission_count}</td>
                    <td>{r.enrollment_count}</td>
                    <td style={{ color: 'var(--primary-color)', fontWeight: 600 }}>KES {Number(r.total_commission || 0).toFixed(2)}</td>
                  </tr>
                )) : (
                  <tr><td colSpan={6} style={{ textAlign: 'center', padding: '40px', color: 'var(--text-secondary)' }}>No recruiter data available</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      );
    }

    if (activeTab === 'regional') {
      const rows = regionalData.map(r => [r.region, r.count]);
      return (
        <div className="card">
          <div className="card-header">
            <div><h2 className="card-title">Regional Distribution</h2><p className="card-subtitle">Active recruiters by county</p></div>
            <button className="btn btn-outline btn-sm" onClick={() => downloadCSV(rows, ['County', 'Recruiters'], `regional_distribution_${new Date().toISOString().split('T')[0]}.csv`)}>Export CSV</button>
          </div>
          <div className="table-container">
            <table className="table">
              <thead><tr><th>County</th><th>Recruiters</th></tr></thead>
              <tbody>
                {regionalData.length ? regionalData.map((r, i) => (
                  <tr key={i}><td style={{ fontWeight: 600 }}>{r.region}</td><td>{r.count}</td></tr>
                )) : (
                  <tr><td colSpan={2} style={{ textAlign: 'center', padding: '40px', color: 'var(--text-secondary)' }}>No regional data available</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      );
    }

    if (activeTab === 'growth') {
      const maxRev = Math.max(1, ...monthly.map(m => Number(m.revenue || 0)));
      const rows = monthly.map(m => [m.month_name || m.month, m.admissions || 0, m.enrollments || 0, `KES ${Number(m.revenue || 0).toFixed(2)}`]);
      return (
        <div className="card">
          <div className="card-header">
            <div><h2 className="card-title">Growth Trends</h2><p className="card-subtitle">Monthly admissions, enrollments and revenue (last 6 months)</p></div>
            <button className="btn btn-outline btn-sm" onClick={() => downloadCSV(rows, ['Month', 'Admissions', 'Enrollments', 'Revenue'], `growth_trends_${new Date().toISOString().split('T')[0]}.csv`)}>Export CSV</button>
          </div>
          <div className="table-container">
            <table className="table">
              <thead><tr><th>Month</th><th>Admissions</th><th>Enrollments</th><th>Revenue</th></tr></thead>
              <tbody>
                {monthly.length ? monthly.map((m, i) => (
                  <tr key={i}>
                    <td style={{ fontWeight: 600 }}>{m.month_name || m.month}</td>
                    <td>{m.admissions || 0}</td>
                    <td>{m.enrollments || 0}</td>
                    <td style={{ color: 'var(--primary-color)', fontWeight: 600 }}>KES {Number(m.revenue || 0).toFixed(2)}</td>
                  </tr>
                )) : (
                  <tr><td colSpan={4} style={{ textAlign: 'center', padding: '40px', color: 'var(--text-secondary)' }}>No growth trend data available</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <div style={{ padding: '8px 24px 24px' }}>
            <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 12, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Revenue Trend</div>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, height: 160, padding: '0 4px' }}>
              {monthly.map((m, i) => (
                <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', height: '100%' }}>
                  <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: 4 }}>KES {Number(m.revenue || 0).toLocaleString()}</div>
                  <div title={`KES ${Number(m.revenue || 0).toFixed(2)}`} style={{ width: '100%', height: `${(Number(m.revenue || 0) / maxRev) * 100}%`, minHeight: 4, background: 'linear-gradient(180deg, var(--secondary-color), var(--primary-color))', borderRadius: '6px 6px 0 0' }} />
                  <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: 6 }}>{m.month_name || m.month}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <div className="main-content">
      <div className="page-header">
        <div>
          <h1 className="page-title">{title}</h1>
          <p className="page-subtitle">{subtitle}</p>
        </div>
      </div>

      {/* Filter bar */}
      <div className="card" style={{ marginBottom: '24px' }}>
        <div className="card-header">
          <div><h2 className="card-title">Filters</h2><p className="card-subtitle">{periodLabel || 'Select a date range'}</p></div>
        </div>
        <div style={{ padding: '4px 24px 24px' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {RANGES.map(r => (
              <button
                key={r.value}
                onClick={() => setRange(r.value)}
                className="btn btn-sm"
                style={{
                  border: `1px solid ${range === r.value ? 'var(--secondary-color)' : 'var(--border-color)'}`,
                  background: range === r.value ? 'var(--secondary-color)' : 'transparent',
                  color: range === r.value ? '#fff' : 'var(--text-secondary)',
                  borderRadius: '999px',
                  padding: '6px 14px'
                }}
              >
                {r.label}
              </button>
            ))}
          </div>
          {range === 'custom' && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', marginTop: '16px', alignItems: 'flex-end' }}>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Start Date</label>
                <input type="date" className="form-input" value={startDate} onChange={e => setStartDate(e.target.value)} />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">End Date</label>
                <input type="date" className="form-input" value={endDate} onChange={e => setEndDate(e.target.value)} />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '4px', borderBottom: '1px solid var(--border-color)', marginBottom: '24px', flexWrap: 'wrap' }}>
        {TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setActiveTab(t.id)}
            style={{
              background: 'none',
              border: 'none',
              borderBottom: `2px solid ${activeTab === t.id ? 'var(--primary-color)' : 'transparent'}`,
              color: activeTab === t.id ? 'var(--primary-color)' : 'var(--text-secondary)',
              fontWeight: 600,
              padding: '12px 18px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {renderTabContent()}
    </div>
  );
}
